import React from 'react';
import { Navigate } from 'react-router-dom';
import { getUserRole, ROLES } from '../../utils/roles';

/**
 * Route-to-role access map
 * Defines which role IDs can access each route path.
 * Routes not listed here are accessible to all authenticated users.
 */
export const ROUTE_PERMISSIONS: Record<string, number[]> = {
  // Owner-only routes
  '/analytics': [ROLES.OWNER],
  '/admin/sales-analytics': [ROLES.OWNER],
  '/products': [ROLES.OWNER],
  '/modules': [ROLES.OWNER],
  '/plans': [ROLES.OWNER],
  '/users': [ROLES.OWNER],
  '/activity-logs': [ROLES.OWNER],
  '/system/health': [ROLES.OWNER],
  '/db-pools': [ROLES.OWNER],
  '/consultants': [ROLES.OWNER],
  '/associations': [ROLES.OWNER],
  '/industries': [ROLES.OWNER],
  '/prospects': [ROLES.OWNER],

  // Owner + Distributor
  '/distributors': [ROLES.OWNER],
  '/resellers': [ROLES.OWNER, ROLES.DISTRIBUTOR],
  '/distributor/add-funds': [ROLES.OWNER, ROLES.DISTRIBUTOR],
  '/distributor/campaigns': [ROLES.OWNER, ROLES.DISTRIBUTOR],

  // Owner + Distributor + Reseller
  '/customers': [ROLES.OWNER, ROLES.DISTRIBUTOR, ROLES.RESELLER],
  '/campaigns': [ROLES.OWNER, ROLES.DISTRIBUTOR, ROLES.RESELLER],
  '/credits': [ROLES.OWNER, ROLES.DISTRIBUTOR, ROLES.RESELLER],
  '/reseller/add-funds': [ROLES.OWNER, ROLES.RESELLER],
  '/reseller/campaigns': [ROLES.OWNER, ROLES.RESELLER],

  // Owner + Distributor + Reseller (Dashboard — customers see subscriptions instead)
  '/dashboard': [ROLES.OWNER, ROLES.DISTRIBUTOR, ROLES.RESELLER],

  // All roles except Customer
  '/payments': [ROLES.OWNER, ROLES.DISTRIBUTOR, ROLES.RESELLER],

  // Customer-specific
  '/customer/add-funds': [ROLES.OWNER, ROLES.CUSTOMER],

  // Developer/utility pages. These must be listed explicitly: an unlisted path
  // falls through to `return true` below, i.e. open to every authenticated user.
  // '/sample-page' renders the SAME component as the owner-gated
  // '/system/health', so leaving it unlisted let any role (including Customer)
  // reach System Health just by typing the other URL.
  '/sample-page': [ROLES.OWNER],
  '/utils/typography': [ROLES.OWNER],
  '/utils/color': [ROLES.OWNER],
  '/utils/shadow': [ROLES.OWNER],
  '/utils/icons/material': [ROLES.OWNER],
  '/utils/icons/tabler': [ROLES.OWNER],

  // Accessible to all roles (no entry needed, but listed for clarity):
  // '/subscriptions', '/orders', '/profile', '/settings'
};

/**
 * Check if a given role has access to a specific route path
 */
export const hasRouteAccess = (path: string, roleId: number): boolean => {
  const allowedRoles = ROUTE_PERMISSIONS[path];
  
  // If no permission entry exists, the route is accessible to all authenticated users
  if (!allowedRoles) return true;
  
  return allowedRoles.includes(roleId);
};

/**
 * Get the default redirect path for a role when they try to access an unauthorized route
 */
const getDefaultRoute = (roleId: number): string => {
  return roleId === ROLES.CUSTOMER ? '/subscriptions' : '/dashboard';
};

interface RoleGuardProps {
  /** The route path being guarded */
  path: string;
  /** The child element/component to render if access is allowed */
  children: React.ReactElement;
}

/**
 * RoleGuard Component
 * 
 * Wraps a route element and checks if the current user's role has permission to access it.
 * If not, redirects to the user's default page (dashboard or subscriptions for customers).
 * 
 * Usage in routes:
 *   { path: '/analytics', element: <RoleGuard path="/analytics">{withSuspense(Analytics)}</RoleGuard> }
 */
const RoleGuard: React.FC<RoleGuardProps> = ({ path, children }) => {
  const roleId = getUserRole();
  
  if (!hasRouteAccess(path, roleId)) {
    const defaultRoute = getDefaultRoute(roleId);
    return <Navigate to={defaultRoute} replace />;
  }
  
  return children;
};

export default RoleGuard;
export { RoleGuard };
